/**
 * Carrito Flotante Sidebar - JavaScript
 * El estado vacío/lleno se controla con la clase "is-empty" en el sidebar;
 * el CSS se encarga de mostrar/ocultar cada bloque.
 */

class FloatingCart {
  constructor() {
    this.cart = null;
    this.isOpen = false;
    this.isUpdating = false;
    this.autoCloseTimer = null;

    this.init();
  }

  init() {
    this.bindEvents();
    this.initStickyBar();
    this.loadCartData();
  }

  bindEvents() {
    // Open cart
    const trigger = document.getElementById('floating-cart-trigger');
    if (trigger) {
      trigger.addEventListener('click', () => this.openCart());
    }

    // Close cart
    const closeBtn = document.getElementById('floating-cart-close');
    const overlay = document.getElementById('floating-cart-overlay');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.closeCart());
    }

    if (overlay) {
      overlay.addEventListener('click', () => this.closeCart());
    }

    // Cerrar con tecla Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.closeCart();
      }
    });

    // Delegación de eventos para elementos dinámicos
    const cartContainer = document.getElementById('floating-cart-items');
    if (cartContainer) {
      cartContainer.addEventListener('click', (e) => this.handleCartItemClick(e));
    }

    // Checkout button
    const checkoutBtn = document.getElementById('floating-cart-checkout');
    if (checkoutBtn) {
      checkoutBtn.addEventListener('click', () => this.proceedToCheckout());
    }

    // Listen for cart changes from other components
    document.addEventListener('cart:updated', () => {
      this.loadCartData();
    });
  }

  /**
   * Barra sticky superior (móvil): se muestra al hacer scroll hacia abajo,
   * una vez que el header del tema salió de pantalla.
   */
  initStickyBar() {
    this.stickyBar = document.getElementById('floating-cart-sticky-bar');
    if (!this.stickyBar) return;

    const stickyTrigger = document.getElementById('floating-cart-sticky-trigger');
    if (stickyTrigger) {
      stickyTrigger.addEventListener('click', () => this.openCart());
    }

    this.cloneHeaderLogo();
    this.measureStickyThreshold();

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        this.updateStickyBar();
      });
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', () => {
      this.measureStickyThreshold();
      this.updateStickyBar();
    }, { passive: true });
    window.addEventListener('load', () => {
      this.measureStickyThreshold();
      this.updateStickyBar();
    });

    this.updateStickyBar();
  }

  /** Umbral de scroll = alto del header del tema (con mínimo razonable) */
  measureStickyThreshold() {
    const header = document.querySelector('.shopify-section--header') ||
      document.querySelector('#mobile_nav');
    const height = header ? header.getBoundingClientRect().height : 0;
    this.stickyThreshold = height > 0 ? Math.max(80, height - 40) : 140;
  }

  updateStickyBar() {
    if (!this.stickyBar) return;

    // The sticky bar only ever renders on mobile (see the max-width:768px
    // block in floating-cart.css). Without this guard, scrolling past the
    // threshold on desktop still flagged it "is-visible" — getActiveTrigger()
    // would then hand callers a display:none element with a zero-size rect.
    const isMobileViewport = window.innerWidth <= 768;
    const shouldShow = isMobileViewport && window.pageYOffset > (this.stickyThreshold || 140);

    this.stickyBar.classList.toggle('is-visible', shouldShow);
    this.stickyBar.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    document.body.classList.toggle('floating-cart-sticky-active', shouldShow);
  }

  /** Reutiliza el logo del header del tema dentro de la barra sticky */
  cloneHeaderLogo() {
    const holder = this.stickyBar && this.stickyBar.querySelector('[data-sticky-logo]');
    if (!holder) return;

    const source = document.querySelector('.header-logo .logo--mobile img') ||
      document.querySelector('.header-logo img');
    if (!source) return;

    const img = document.createElement('img');
    img.src = source.currentSrc || source.src;
    img.alt = source.alt || '';
    img.decoding = 'async';

    holder.textContent = '';
    holder.appendChild(img);
  }

  /** Botón de carrito visible en este momento (barra sticky o botón flotante) */
  getActiveTrigger() {
    if (this.stickyBar && this.stickyBar.classList.contains('is-visible')) {
      const stickyTrigger = document.getElementById('floating-cart-sticky-trigger');
      // offsetParent is null for display:none elements — guards against
      // animating toward a trigger that isn't actually rendered.
      if (stickyTrigger && stickyTrigger.offsetParent !== null) return stickyTrigger;
    }
    return document.getElementById('floating-cart-trigger');
  }

  async loadCartData() {
    try {
      const response = await fetch('/cart.js');
      this.cart = await response.json();
      this.renderCart();
    } catch (error) {
      console.error('Error loading cart data:', error);
    }
  }

  renderCart() {
    if (!this.cart) return;

    const sidebar = document.getElementById('floating-cart-sidebar');
    const itemsContainer = document.getElementById('floating-cart-items');
    const headerCount = document.getElementById('floating-cart-header-count');

    if (sidebar) {
      sidebar.classList.toggle('is-empty', this.cart.item_count === 0);
    }

    if (headerCount) {
      headerCount.textContent = `(${this.cart.item_count})`;
    }

    this.updateCartCount();

    if (!itemsContainer) return;

    if (this.cart.item_count === 0) {
      itemsContainer.innerHTML = '';
      this.renderUpsell();
      return;
    }

    itemsContainer.innerHTML = this.cart.items.map((item, index) => `
      <div class="floating-cart-item" data-line-item="${index + 1}">
        <div class="cart-item-image">
          ${item.image ? `
            <a href="${item.url}">
              <img src="${item.image}" alt="${this.escapeHtml(item.title)}" loading="lazy">
            </a>
          ` : `
            <div class="cart-item-placeholder">
              <span class="placeholder-icon">📦</span>
            </div>
          `}
        </div>

        <div class="cart-item-details">
          <h4 class="cart-item-title">
            <a href="${item.url}">${this.escapeHtml(item.product_title)}</a>
          </h4>

          ${item.variant_title && item.variant_title !== 'Default Title' ? `
            <p class="cart-item-variant">${this.escapeHtml(item.variant_title)}</p>
          ` : ''}

          <div class="cart-item-price">
            ${item.line_level_discount_allocations.length > 0 ? `
              <span class="price-sale">${this.formatMoney(item.final_price)}</span>
              <span class="price-original">${this.formatMoney(item.original_price)}</span>
            ` : `
              <span class="price-regular">${this.formatMoney(item.original_price)}</span>
            `}
          </div>

          <div class="cart-item-actions">
            <div class="cart-item-quantity">
              <button class="quantity-btn quantity-minus" data-line="${index + 1}" data-func="minus" aria-label="Decrease quantity">
                <span class="icon-minus" aria-hidden="true"></span>
              </button>
              <span class="quantity-value" aria-label="Quantity">${item.quantity}</span>
              <button class="quantity-btn quantity-plus" data-line="${index + 1}" data-func="plus" aria-label="Increase quantity">
                <span class="icon-plus" aria-hidden="true"></span>
              </button>
            </div>

            <button class="remove-item-btn" data-line="${index + 1}" aria-label="Remove product" title="Remove product">
              <span class="remove-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M3 6H5H21" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M10 11V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M14 11V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
              </span>
            </button>
          </div>
        </div>
      </div>
    `).join('');

    this.updateCartSummary();
    this.renderUpsell();
  }

  /**
   * "Complete your purchase" (LRLUX search app): recommendations for what is in
   * the cart, from GET /apps/op-buscador/recommend?handles=… — real, in-stock
   * products only (tester/original version, same brand, similar scent). Enabled by
   * Theme settings → Floating cart → "Show Complete your purchase" (the container
   * exists only when on). If the app answers nothing or fails, the box stays hidden.
   */
  renderUpsell() {
    const box = document.getElementById('floating-cart-upsell');
    const list = document.getElementById('floating-cart-upsell-list');
    if (!box || !list || !this.cart) return;

    const handles = [...new Set(this.cart.items.map((item) => item.handle).filter(Boolean))];
    if (handles.length === 0) {
      box.hidden = true;
      list.innerHTML = '';
      this.upsellKey = '';
      return;
    }

    const key = handles.join(',');
    if (key === this.upsellKey) return;
    this.upsellKey = key;

    if (!this.upsellBound) {
      this.upsellBound = true;
      list.addEventListener('click', (event) => {
        const button = event.target.closest('[data-upsell-add]');
        if (button) this.addUpsellItem(button);
      });
    }

    const limit = box.dataset.limit || '3';
    fetch('/apps/op-buscador/recommend?handles=' + encodeURIComponent(key) + '&limit=' + encodeURIComponent(limit))
      .then((res) => {
        if (!res.ok) throw new Error('recommend ' + res.status);
        return res.json();
      })
      .then((data) => {
        if (this.upsellKey !== key) return;
        const recs = (data.recommendations || []).filter((rec) => rec.product && rec.product.variant_id && rec.product.in_stock !== false);
        if (recs.length === 0) {
          box.hidden = true;
          list.innerHTML = '';
          return;
        }
        list.innerHTML = recs.map((rec) => {
          const p = rec.product;
          const old = p.compare_at_price && p.compare_at_price > p.price
            ? `<s class="floating-cart-upsell__old">${this.formatMoney(Math.round(p.compare_at_price * 100))}</s>` : '';
          return `
            <div class="floating-cart-upsell__item">
              <a class="floating-cart-upsell__img" href="${this.escapeHtml(p.url)}">
                ${p.image ? `<img src="${this.escapeHtml(p.image)}" alt="${this.escapeHtml(p.title)}" loading="lazy" width="64" height="64">` : ''}
              </a>
              <div class="floating-cart-upsell__info">
                <a class="floating-cart-upsell__name" href="${this.escapeHtml(p.url)}">${this.escapeHtml(p.title)}</a>
                <span class="floating-cart-upsell__price"><strong>${this.formatMoney(Math.round(p.price * 100))}</strong>${old}</span>
              </div>
              <button type="button" class="floating-cart-upsell__add" data-upsell-add data-variant="${this.escapeHtml(p.variant_id)}">Add</button>
            </div>`;
        }).join('');
        box.hidden = false;
      })
      .catch(() => {
        box.hidden = true;
        this.upsellKey = '';
      });
  }

  /** Adds an upsell product WITHOUT the auto-close of addToCart(), so the customer can keep adding. */
  async addUpsellItem(button) {
    const variantId = Number(button.dataset.variant);
    if (!variantId || button.disabled || this.isUpdating) return;
    const original = button.textContent;
    button.disabled = true;
    button.textContent = '…';
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: 1 })
      });
      if (!response.ok) throw new Error('add failed');
      await this.loadCartData();
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: this.cart, variantId, quantity: 1 } }));
    } catch (error) {
      button.textContent = 'Error';
      setTimeout(() => { button.textContent = original; button.disabled = false; }, 1500);
    }
  }

  updateCartSummary() {
    const subtotalElement = document.querySelector('.subtotal-amount');
    if (subtotalElement) {
      subtotalElement.textContent = this.formatMoney(this.cart.total_price);
    }

    const discountRow = document.getElementById('cart-discount-row');
    const discountAmount = document.getElementById('cart-discount-amount');
    if (discountRow && discountAmount) {
      if (this.cart.total_discount > 0) {
        discountRow.style.display = 'flex';
        discountAmount.textContent = `-${this.formatMoney(this.cart.total_discount)}`;
      } else {
        discountRow.style.display = 'none';
      }
    }

    this.updateFreeShippingMessage();
  }

  updateFreeShippingMessage() {
    const messageElement = document.getElementById('free-shipping-message');
    if (!messageElement) return;

    if (!window.floatingCartSettings || !window.floatingCartSettings.enableFreeShippingMessage) {
      messageElement.style.display = 'none';
      return;
    }

    const freeShippingThreshold = window.floatingCartSettings.freeShippingThreshold || 9999;
    const freeShippingMessage = window.floatingCartSettings.freeShippingMessage || 'Add {{amount}} more for free shipping';
    const freeShippingAchievedMessage = window.floatingCartSettings.freeShippingAchievedMessage || '🎉 You qualify for free shipping!';

    const currentTotal = this.cart.total_price;
    const messageTextElement = document.getElementById('shipping-message-text');
    const progressFillElement = document.getElementById('shipping-progress-fill');

    if (!messageTextElement || !progressFillElement) return;

    messageElement.style.display = 'block';

    if (currentTotal >= freeShippingThreshold) {
      messageTextElement.textContent = freeShippingAchievedMessage;
      progressFillElement.style.width = '100%';
    } else {
      const remaining = freeShippingThreshold - currentTotal;
      const message = freeShippingMessage.replace(/\{\{amount\}\}/g, this.formatMoney(remaining));
      messageTextElement.textContent = message;

      const progressPercentage = (currentTotal / freeShippingThreshold) * 100;
      progressFillElement.style.width = `${Math.min(progressPercentage, 100)}%`;
    }
  }

  handleCartItemClick(e) {
    const target = e.target.closest('[data-line]');
    if (!target) return;

    const line = parseInt(target.dataset.line);
    const func = target.dataset.func;

    if (func === 'minus' || func === 'plus') {
      e.preventDefault();
      this.updateQuantity(line, func);
    } else if (target.classList.contains('remove-item-btn')) {
      e.preventDefault();
      this.removeItem(line);
    }
  }

  async updateQuantity(line, func) {
    if (this.isUpdating) return;

    const item = this.cart.items[line - 1];
    if (!item) return;

    let newQuantity = item.quantity;
    if (func === 'plus') {
      newQuantity++;
    } else if (func === 'minus') {
      newQuantity = Math.max(0, newQuantity - 1);
    }

    if (newQuantity === 0) {
      this.removeItem(line);
    } else {
      this.updateQuantityTo(line, newQuantity);
    }
  }

  async updateQuantityTo(line, quantity) {
    if (this.isUpdating) return;
    if (!this.cart || !this.cart.items[line - 1]) return;

    this.isUpdating = true;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: quantity
        })
      });

      if (response.ok) {
        await this.loadCartData();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } else {
        throw new Error('Error updating quantity');
      }
    } catch (error) {
      console.error('Error updating quantity:', error);
      this.showNotification('Error updating quantity', 'error');
    } finally {
      this.isUpdating = false;
    }
  }

  async removeItem(line) {
    if (this.isUpdating) return;

    this.isUpdating = true;
    const itemElement = document.querySelector(`[data-line-item="${line}"]`);

    if (itemElement) {
      itemElement.classList.add('removing');
    }

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          line: line,
          quantity: 0
        })
      });

      if (response.ok) {
        await this.loadCartData();
        document.dispatchEvent(new CustomEvent('cart:updated'));
      } else {
        throw new Error('Error removing item');
      }
    } catch (error) {
      console.error('Error removing item:', error);
      this.showNotification('Error removing product', 'error');

      if (itemElement) {
        itemElement.classList.remove('removing');
      }
    } finally {
      this.isUpdating = false;
    }
  }

  async addToCart(variantId, quantity = 1, options = {}) {
    if (this.isUpdating) return;

    this.isUpdating = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: variantId,
          quantity: quantity
        })
      });

      if (response.ok) {
        const responseData = await response.json();

        await this.loadCartData();

        // Abre el carrito flotante al agregar un producto y lo cierra solo
        // a los 2s — mismo comportamiento para cualquier caller (LRLUX y el
        // resto del tema pasan todos por este método).
        this.openCart(2000);

        // Mostrar la animación de "fly to cart"
        // (assets/product-to-cart-animation.js) — el mismo sistema
        // compartido que usa el PDP, para cualquier llamador que pase un
        // `sourceImage` (resuelto por el caller vía
        // window.resolveProductCardImage() en el momento del click, antes
        // de este request async — ver snippets/floating-cart-integration.liquid
        // y snippets/floating-cart-handler.liquid).
        // `deferCartAnimation` lo usan callers que ya disparan esta misma
        // animación por su cuenta desde otro evento (el <product-form> del
        // PDP, vía product-form:submit:success) — evita que ambos disparen
        // la animación para el mismo add.
        if (!options.deferCartAnimation) {
          if (typeof window.animateProductToCart === 'function') {
            window.animateProductToCart(options.sourceImage || null);
          } else {
            this.animateFloatingButton();
          }
        }

        document.dispatchEvent(new CustomEvent('cart:updated', {
          detail: {
            cart: this.cart,
            addedItem: responseData,
            variantId: variantId,
            quantity: quantity
          }
        }));

        return responseData;
      } else {
        const error = await response.json();
        throw new Error(error.message || 'Error adding to cart');
      }
    } catch (error) {
      console.error('Error adding to cart:', error);
      this.showNotification('Error adding product', 'error');
      throw error;
    } finally {
      this.isUpdating = false;
    }
  }

  updateCartCount() {
    const count = this.cart ? this.cart.item_count : 0;

    const badge = document.getElementById('floating-cart-badge');
    if (badge) {
      badge.textContent = count;
      badge.style.display = count > 0 ? 'flex' : 'none';
    }

    const stickyBadge = document.getElementById('floating-cart-sticky-badge');
    if (stickyBadge) {
      stickyBadge.textContent = count;
      stickyBadge.style.display = count > 0 ? 'flex' : 'none';
    }

    // Contadores del header del tema
    const countElements = document.querySelectorAll('.cart-count');
    countElements.forEach(element => {
      element.textContent = count;
      element.style.display = count > 0 ? 'inline' : 'none';
    });
  }

  /** @param {number|null} autoCloseMs - si se pasa, el carrito se cierra solo tras ese tiempo */
  openCart(autoCloseMs = null) {
    const sidebar = document.getElementById('floating-cart-sidebar');
    if (sidebar) {
      sidebar.classList.add('active');
      sidebar.setAttribute('aria-hidden', 'false');
      this.isOpen = true;
      document.body.classList.add('floating-cart-open');

      const closeBtn = document.getElementById('floating-cart-close');
      if (closeBtn) closeBtn.focus();
    }

    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }
    if (autoCloseMs) {
      this.autoCloseTimer = setTimeout(() => this.closeCart(), autoCloseMs);
    }
  }

  closeCart() {
    if (this.autoCloseTimer) {
      clearTimeout(this.autoCloseTimer);
      this.autoCloseTimer = null;
    }

    const sidebar = document.getElementById('floating-cart-sidebar');
    if (sidebar) {
      sidebar.classList.remove('active');
      sidebar.setAttribute('aria-hidden', 'true');
      this.isOpen = false;
      document.body.classList.remove('floating-cart-open');
    }
  }

  proceedToCheckout() {
    window.location.href = '/checkout';
  }

  animateFloatingButton() {
    const trigger = this.getActiveTrigger();
    if (trigger) {
      trigger.classList.add('new-item');
      setTimeout(() => {
        trigger.classList.remove('new-item');
      }, 600);
    }
  }

  formatMoney(cents) {
    const currencyType = window.floatingCartSettings?.currencyType || 'en-US';
    return new Intl.NumberFormat(currencyType, {
      style: 'currency',
      currency: (this.cart && this.cart.currency) || 'EUR'
    }).format(cents / 100);
  }

  escapeHtml(text) {
    if (text == null) return '';
    return String(text)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  showNotification(message, type = 'info') {
    // Notificaciones desactivadas temporalmente (se superponían con otros elementos).
    // Para reactivarlas, eliminar este return.
    return;

    const notification = document.createElement('div');
    notification.className = `floating-cart-notification ${type}`;
    notification.textContent = message;

    // Colocada encima del botón flotante para no taparlo
    Object.assign(notification.style, {
      position: 'fixed',
      bottom: 'calc(90px + env(safe-area-inset-bottom, 0px))',
      right: '20px',
      padding: '12px 20px',
      borderRadius: '6px',
      color: 'white',
      fontWeight: '500',
      zIndex: '2147483601',
      transform: 'translateX(120%)',
      transition: 'transform 0.3s ease',
      backgroundColor: type === 'success' ? '#28a745' : type === 'error' ? '#dc3545' : '#007bff',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      maxWidth: '300px',
      wordWrap: 'break-word'
    });

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.transform = 'translateX(0)';
    }, 100);

    setTimeout(() => {
      notification.style.transform = 'translateX(120%)';
      setTimeout(() => {
        if (notification.parentNode) {
          notification.parentNode.removeChild(notification);
        }
      }, 300);
    }, 3000);
  }
}

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  window.floatingCart = new FloatingCart();
});

// Global function to add products to cart from other components.
// Returns the addToCart() promise — callers (e.g. floating-cart-product-buttons.liquid,
// sections/main-product-lrlux.liquid) rely on .then()/.catch() to know whether the
// request actually succeeded.
window.addToFloatingCart = function(variantId, quantity = 1, options = {}) {
  if (window.floatingCart) {
    return window.floatingCart.addToCart(variantId, quantity, options);
  }
  return Promise.reject(new Error('Floating cart is not available'));
};

// Global function to open floating cart
window.openFloatingCart = function() {
  if (window.floatingCart) {
    window.floatingCart.openCart();
  }
};

// Global function to close floating cart
window.closeFloatingCart = function() {
  if (window.floatingCart) {
    window.floatingCart.closeCart();
  }
};
